import React from 'react';

type AlertKind = 'info' | 'success' | 'warning' | 'error';

function classesFor(kind: AlertKind) {
  switch (kind) {
    case 'success':
      return { wrap: 'border border-green-200 bg-green-50 text-green-800', badge: 'bg-green-600 text-white' };
    case 'warning':
      return { wrap: 'border border-amber-200 bg-amber-50 text-amber-900', badge: 'bg-amber-600 text-white' };
    case 'error':
      return { wrap: 'border border-red-200 bg-red-50 text-red-800', badge: 'bg-red-600 text-white' };
    case 'info':
    default:
      return { wrap: 'border border-blue-200 bg-blue-50 text-blue-900', badge: 'bg-blue-600 text-white' };
  }
}

/** Named: generic alert */
export function FormAlert({
  kind = 'info',
  title,
  message,
  children,
  onClose,
}: {
  kind?: AlertKind;
  title?: React.ReactNode;
  message?: React.ReactNode;
  children?: React.ReactNode;
  onClose?: () => void;
}) {
  const cls = classesFor(kind);
  return (
    <div className={`rounded-xl p-4 ${cls.wrap}`}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${cls.badge}`}>
          {kind.toUpperCase()}
        </span>
        <div className="flex-1">
          {title && <div className="font-medium">{title}</div>}
          {message && <div className="text-sm mt-0.5">{message}</div>}
          {children}
        </div>
        {onClose && (
          <button type="button" className="text-sm opacity-70 hover:opacity-100" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>
    </div>
  );
}

/** Named: field-level validation helper */
export function FieldError({
  errors,
  name,
}: {
  errors?: Record<string, string[] | string> | null;
  name: string;
}) {
  if (!errors) return null;
  const list = errors[name];
  const msg = Array.isArray(list) ? list[0] : list;
  if (!msg) return null;
  return <div className="mt-1 text-sm text-red-600">{msg}</div>;
}

/** Default: simple error block */
export default function FormError({ message }: { message?: React.ReactNode }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 p-3 text-sm">
      {message}
    </div>
  );
}
